import type { Column } from '@tanstack/react-table';
import { ChevronDownIcon, ChevronsUpDownIcon, ChevronUpIcon, EyeOff } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import {
  Button,
  type ButtonProps,
  Icon,
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
  TableHead,
  Text,
} from '@/_components';
import { contentAlignmentStyle, textAlignmentStyle, twSort } from '@/_shared';
import type { DivProps } from '@/types';

import type { SortingProps } from './toolbar';

const headerGlobalButtonsStyle = twSort`hover:text-accent dark:hover:text-accent-x-light text-c-card-foreground -ml-1 h-auto px-2 text-sm font-semibold`;

type ColumnHeaderMeta = {
  name: string;
  sortKey?: string;
};

function getColumnSortField<TData, TValue>(column: Column<TData, TValue>) {
  const meta = column.columnDef.meta as ColumnHeaderMeta | undefined;
  return meta?.sortKey ?? column.id;
}

interface DataTableColumnHeaderProps<TData, TValue> extends DivProps {
  column: Column<TData, TValue>;
  sorting?: SortingProps;
  sortingAlign?: 'start' | 'center' | 'end';
  align?: 'left' | 'center' | 'right';
  headerTextClassName?: string;
}

export function ColumnHeader<TData, TValue>({
  column,
  className,
  sorting,
  align = 'left',
  sortingAlign,
  headerTextClassName = '',
}: DataTableColumnHeaderProps<TData, TValue>) {
  const meta = column.columnDef.meta as ColumnHeaderMeta | undefined;
  const sortField = getColumnSortField(column);

  if (!column.getCanSort()) {
    return <TableHeadText title={meta?.name ?? ''} align={align} className={className} />;
  }

  return (
    <Menubar tabIndex={-1} className={twMerge('flex items-center', contentAlignmentStyle[align], className)}>
      <MenubarMenu>
        <MenubarTrigger asChild>
          <Button
            tag="div"
            as="link"
            size="sm"
            iconRight={
              sorting?.sort === sortField
                ? ChevronUpIcon
                : sorting?.sort === `-${sortField}`
                  ? ChevronDownIcon
                  : ChevronsUpDownIcon
            }
            className="px-2"
            iconClassName="text-c-card-foreground size-4"
          >
            <TableHeadText
              title={meta?.name ?? ''}
              align={align}
              className={twMerge('line-clamp-1 text-left text-wrap', headerTextClassName)}
            />
          </Button>
        </MenubarTrigger>

        <MenubarContent align={sortingAlign}>
          <MenubarItem onClick={() => setTimeout(() => sorting?.setSort(sortField), 0)}>
            <Icon icon={ChevronUpIcon} className="size-4" />
            <span>Asc</span>
          </MenubarItem>
          <MenubarItem onClick={() => setTimeout(() => sorting?.setSort(`-${sortField}`), 0)}>
            <Icon icon={ChevronDownIcon} className="size-4" />
            <span>Desc</span>
          </MenubarItem>
          {column.getCanHide() && (
            <>
              <MenubarSeparator className="my-2" />
              <MenubarItem onClick={() => setTimeout(() => column.toggleVisibility(false), 0)}>
                <Icon icon={EyeOff} className="size-4" />
                <span>Hide</span>
              </MenubarItem>
            </>
          )}
        </MenubarContent>
      </MenubarMenu>
    </Menubar>
  );
}

export function TableSortableHeading({
  title,
  headerButtonsStyle = '',
  icon,
  handleSort,
  className,
  headerClassName,
}: {
  title: string;
  headerButtonsStyle?: string;
  icon?: ButtonProps['iconRight'];
  handleSort: () => void;
  className?: string;
  headerClassName?: string;
}) {
  return (
    <TableHead className={headerClassName}>
      <Button
        tag="div"
        as="link"
        size="sm"
        iconRight={icon}
        iconClassName="text-c-card-foreground size-4"
        className={twMerge(headerGlobalButtonsStyle, headerButtonsStyle, 'ml-0 px-2')}
        onClick={handleSort}
      >
        <TableHeadText title={title} className={twMerge('text-left whitespace-nowrap', className)} />
      </Button>
    </TableHead>
  );
}

export function TableHeading({
  title,
  align = 'left',
  className,
  headerClassName,
}: {
  title: string;
  align?: 'left' | 'center' | 'right';
  className?: string;
  headerClassName?: string;
}) {
  return (
    <TableHead className={headerClassName}>
      <TableHeadText title={title} className={className} align={align} />
    </TableHead>
  );
}

export function TableHeadText({
  title,
  align = 'left',
  className,
}: {
  title: string;
  align?: 'left' | 'center' | 'right';
  className?: string;
}) {
  return (
    <Text
      as="small"
      className={twMerge('text-c-card-foreground leading-tight font-bold', textAlignmentStyle[align], className)}
    >
      {title}
    </Text>
  );
}
